import React, {
  useState,
  Dispatch,
  SetStateAction,
  useEffect,
  ReactNode,
} from "react";
import {
  ColumnFiltersState,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  flexRender,
  useReactTable,
  Cell,
  VisibilityState,
  RowModel,
} from "@tanstack/react-table";
import {
  Table,
  TableHeader,
  TableCell,
  TableRow,
  TableHead,
  TableBody,
} from "..";
import { globalFilterFn } from "../../../lib/functions";
import PaginationButtons from "../../button/PaginationButtons";
import { useTranslation } from "react-i18next";
import ColumnChooserModal from "../../modal/ColumnChooserModal";
import { Button } from "../../button";
import SearchInput from "../../inputs/SearchInput";
import { cn } from "../../../utils/cn";
import {
  DEVICE_PURPOSE_ID,
  DEVICE_TYPE_ID,
  DOC_TYPE_ID,
  EXPORT_TYPES,
} from "../../../constants/common";
import { useTableStore } from "../../../store/Table";
import ExportToExcelButton from "../../button/ExportToExcelPDFButton";

interface TableWrapperProps {
  data: any;
  columns: any;
  search?: string;
  setSearch?: React.Dispatch<React.SetStateAction<string>>;

  isPagination?: boolean;
  page?: number;
  setPage?: Dispatch<SetStateAction<number>>;
  totalPages?: number;
  count?: number;

  isExportExcel?: boolean;
  onExport?: (type: EXPORT_TYPES) => Promise<void>;

  isSearch?: boolean;
  isColumnChooser?: boolean;

  selectedKey?: string;
  selectedRowId?: number;

  hideColumns?: VisibilityState;
  tableId?: string;

  isDarkModeStyles?: boolean;

  isHideStatusIndicator?: boolean;

  rowSelectionId?: string;

  onRowSelection?: (
    selectedRowsModel: RowModel<{
      [key: string]: any;
    }>,
  ) => void;

  ButtonsWrapper?: ReactNode;

  disableDarkModeStyles?: boolean;

  setVisibleColumnKeys?: Dispatch<SetStateAction<string[]>>;

  pageSize?: number;
  setPageSize?: Dispatch<SetStateAction<number>>;

  searchStyles?: string;
  actionsContainerStyles?: string;
}

interface TableCellContentProps {
  cell: Cell<any, any>;
}

const TableWrapper = ({
  data,
  columns,
  search,
  setSearch,
  isPagination = true,
  page,
  setPage,
  totalPages,
  count,
  isSearch = false,
  isColumnChooser = true,
  selectedKey,
  selectedRowId,
  hideColumns = {},
  tableId = "",
  isDarkModeStyles = true,
  isHideStatusIndicator = false,
  rowSelectionId = "",
  onRowSelection,
  ButtonsWrapper,
  disableDarkModeStyles = false,
  isExportExcel = false,
  onExport,
  setVisibleColumnKeys,

  pageSize,
  setPageSize,

  searchStyles,
  actionsContainerStyles,
}: TableWrapperProps) => {
  const { columnVisibility, setColumnVisibility } = useTableStore();

  const [rowSelection, setRowSelection] = useState({});

  const [localColumnVisibility, setLocalColumnVisibility] =
    useState<VisibilityState>(columnVisibility[tableId] || hideColumns);

  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const [isColumnChooserModalOpen, setIsColumnChooserModalOpen] =
    useState<boolean>(false);

  useEffect(() => {
    if (Object.keys(localColumnVisibility).length > 0) {
      setColumnVisibility(tableId, localColumnVisibility);
    }
  }, [localColumnVisibility, setColumnVisibility, tableId]);

  const { t } = useTranslation();

  const table = useReactTable({
    data: data || [],
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setLocalColumnVisibility,
    manualPagination: true,
    state: {
      columnFilters,
      columnVisibility: localColumnVisibility,
      globalFilter: search,
      rowSelection,
    },
    globalFilterFn: globalFilterFn,
    onGlobalFilterChange: setSearch,
    onRowSelectionChange: setRowSelection,
    enableRowSelection: true,
    getRowId: (row) => row[rowSelectionId],
  });

  const selectedRowsModel = table.getSelectedRowModel();

  useEffect(() => {
    if (onRowSelection) {
      onRowSelection(selectedRowsModel);
    }
  }, [onRowSelection, selectedRowsModel]);

  useEffect(() => {
    const visibleColumnKeys = table
      .getAllColumns()
      .filter((col) => col.getIsVisible())
      .map((col) => col.id);

    setVisibleColumnKeys?.(visibleColumnKeys);
  }, [localColumnVisibility, setVisibleColumnKeys, table]);

  return (
    <>
      <div className="flex flex-col gap-3 ">
        <div
          className={cn(
            "flex flex-col gap-2 md:flex-row justify-between md:items-end",
            actionsContainerStyles,
          )}
        >
          {isSearch ? (
            <div className="flex flex-1 w-full">
              <SearchInput
                search={search}
                setSearch={setSearch}
                disableDarkModeStyles={disableDarkModeStyles}
                styles={searchStyles}
              />
            </div>
          ) : (
            <div />
          )}

          <div className="flex flex-col sm:flex-row flex-wrap sm:items-center gap-3">
            {isExportExcel && (
              <div className="flex items-end sm:justify-end">
                <ExportToExcelButton onExport={onExport} />
              </div>
            )}

            {ButtonsWrapper}

            {isColumnChooser && (
              <Button
                onClick={() => setIsColumnChooserModalOpen(true)}
                className={cn(
                  "bg-transparent ring-1 ring-purple-primary dark:ring-white dark:text-white text-purple-primary hover:bg-transparent ",
                  disableDarkModeStyles &&
                    " dark:ring-purple-primary dark:text-purple-primary",
                )}
              >
                {t("columnChooser")}
              </Button>
            )}
          </div>
        </div>

        <div className="rounded-xl overflow-hidden">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => {
                return (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      return (
                        <TableHead key={header.id}>
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext(),
                              )}
                        </TableHead>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        className={cn(
                          selectedKey &&
                            selectedRowId === row.original[selectedKey]
                            ? "bg-purple-light/80"
                            : "",
                        )}
                      >
                        <span className="text-sm">
                          <TableCellContent cell={cell} />
                        </span>
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={columns?.length}>
                    {t("noResults")}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {isPagination && (
        <PaginationButtons
          page={page}
          setPage={setPage}
          totalPages={totalPages}
          count={count}
          isDarkModeStyles={isDarkModeStyles}
          pageSize={pageSize}
          setPageSize={setPageSize}
        />
      )}

      <ColumnChooserModal
        table={table}
        isOpen={isColumnChooserModalOpen}
        onOpenChange={() => setIsColumnChooserModalOpen(false)}
        isHideStatusIndicator={isHideStatusIndicator}
        tableId={tableId}
      />
    </>
  );
};

export default React.memo(TableWrapper);

const TableCellContent = ({ cell }: TableCellContentProps) => {
  const { t } = useTranslation();

  let value;

  switch (cell.column.id) {
    case "IDType":
      // case "idType":
      value = cell.getValue();
      return (
        <span>
          {value === DOC_TYPE_ID.NATIONAL_ID
            ? t("nationalID")
            : value === DOC_TYPE_ID.PASSPORT
              ? t("passport")
              : value === DOC_TYPE_ID.IQAMA
                ? t("iqama")
                : value === DOC_TYPE_ID.VISA
                  ? t("visa")
                  : ""}
        </span>
      );

    case "deviceType":
      value = cell.getValue();
      return (
        <span>
          {value === DEVICE_TYPE_ID.LAPTOP
            ? t("laptop")
            : value === DEVICE_TYPE_ID.PHONE
              ? t("phone")
              : value === DEVICE_TYPE_ID.TABLET
                ? t("tablet")
                : value === DEVICE_TYPE_ID.USB_DRIVE
                  ? t("usbDrive")
                  : flexRender(cell.column.columnDef.cell, cell.getContext())}
        </span>
      );

    case "devicePurpose":
      value = cell.getValue();
      return (
        <span>
          {value === DEVICE_PURPOSE_ID.PRESENTATION
            ? t("presentation")
            : value === DEVICE_PURPOSE_ID.DATA_COLLECTION
              ? t("dataCollection")
              : value === DEVICE_PURPOSE_ID.GENERAL_USE
                ? t("generalUse")
                : flexRender(cell.column.columnDef.cell, cell.getContext())}
        </span>
      );

    default:
      return (
        <span>{flexRender(cell.column.columnDef.cell, cell.getContext())}</span>
      );
  }
};
